// 2048 interface: arrow keys on the board, a swipe on a touch screen, or the four big arrow buttons under the grid
// (no gesture to learn), unlimited undo, a hint, no time limit. Board sizes 4, 5 and 6; the daily is a 4 by 4 with
// the same opening tiles for everyone. The grid is in the server HTML (CLS 0); this fills the tiles in.
import { canMove, deal, DIRS, GOAL, hint, isOver, maxTile, move, SIZES, type Dir, type Size, type T48State } from "./core"
import { randomSeed } from "../lib/rng"
import { readJSON, writeJSON } from "../lib/store"
import { formatLong, isToday, mark } from "../lib/daily"
import { gameEvent } from "../lib/game-events"
import { gameProgress, newAttempt } from "../lib/stats"

export interface T48Daily { game: string; date: string; seed: string }
interface Saved { v: 1; state: T48State; past: T48State[] }
const PREF_KEY = "dt:pref:2048-size"
const bestKey = (size: number): string => `dt:best:2048-${size}`
const KEYS: Record<string, Dir> = { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down" }
const WORDS: Record<Dir, string> = { left: "left", right: "right", up: "up", down: "down" }

export class Twenty48Game {
  private state!: T48State
  private past: T48State[] = []
  private undos = 0
  private hints = 0
  private hinted: Dir | null = null
  private sentFirst = false
  private newAt = -1 // the square of the tile that just appeared, for its pop
  private readonly status = document.querySelector<HTMLElement>("[data-status]")
  private readonly toolbar = document.querySelector<HTMLElement>("[data-toolbar]")
  private readonly sizes = document.querySelector<HTMLElement>("[data-t48-sizes]")
  private readonly grid: HTMLElement
  private readonly scoreEl: HTMLElement
  private readonly bestEl: HTMLElement

  constructor(private readonly el: HTMLElement, private readonly daily?: T48Daily) {
    this.grid = el.querySelector<HTMLElement>("[data-grid]")!
    this.scoreEl = el.querySelector<HTMLElement>("[data-t48-score]")!
    this.bestEl = el.querySelector<HTMLElement>("[data-t48-best]")!
    const size = daily ? 4 : this.validSize(readJSON<number>(PREF_KEY) ?? Number(el.dataset.variant))
    const saved = readJSON<Saved>(this.saveKey(size))
    if (saved?.v === 1 && saved.state?.size === size && (!daily || saved.state.seed === daily.seed)) {
      this.state = saved.state
      this.past = saved.past ?? []
      this.say(isOver(this.state) ? "Welcome back. That game has no moves left. Undo takes back the last move, or choose New game." : "Welcome back. Your game is where you left it.")
    } else {
      this.state = { ...deal(daily?.seed ?? new URLSearchParams(location.search).get("seed") ?? randomSeed(), size), attempt: newAttempt() }
    }
    this.wire()
    this.render()
  }

  private validSize(n: number): Size { return (SIZES as readonly number[]).includes(n) ? (n as Size) : 4 }
  private saveKey(size: number): string {
    return this.daily ? `dt:save:daily:${this.daily.game}:${this.daily.date}:v1` : `dt:save:2048-${size}:v1`
  }
  private get ruleset(): string { return `2048:${this.state.size}` }

  /** Slide the tiles one way, from a key, a swipe or an arrow button. */
  slide(dir: Dir): void {
    const s = this.state
    if (isOver(s)) return this.say("No moves left. Undo takes back the last move, or choose New game.")
    const next = move(s, dir)
    if (next === s) return this.say(`Nothing can slide ${WORDS[dir]}. Try another way.`)
    this.past.push(s)
    if (this.past.length > 300) this.past.shift()
    this.state = next
    this.hinted = null
    this.newAt = next.cells.findIndex((v, i) => v !== 0 && s.cells[i] === 0 && this.slidInto(s, next, dir, i))
    this.persist()
    this.render()
    if (!this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset: this.ruleset, daily: !!this.daily }) }
    const justWon = next.reached && !s.reached
    if (justWon) gameEvent({ type: "end", ruleset: this.ruleset, outcome: "win", moves: next.moves, undos: this.undos, hints: this.hints, deal: next.seed, daily: !!this.daily })
    if (this.daily) mark(this.daily.game, this.daily.date, next.reached ? "won" : "played")
    const gained = next.score - s.score
    if (justWon) return this.say(this.daily ? (isToday(this.daily.date) ? "You made 2048 on today's board! Keep going for a bigger tile, or come back tomorrow." : `You made 2048 on the board for ${formatLong(this.daily.date)}!`) : "You made 2048! Keep going for a bigger tile, or choose New game.")
    if (isOver(next)) return this.say(`No moves left. Score ${next.score.toLocaleString()}, biggest tile ${maxTile(next)}. Undo takes back the last move, or choose New game.`)
    this.say(`Slid ${WORDS[dir]}${gained ? `, ${gained} points` : ""}. ${this.newTileWords()}`)
  }

  /** True when the empty square at i could only have been filled by the new tile, not by a tile sliding in. */
  private slidInto(before: T48State, after: T48State, dir: Dir, i: number): boolean {
    // The new tile is the one square that changed from empty to filled and is not the end of a slide. Simplest
    // sure test: slide the old board again without a spawn and see whether i is still empty there.
    const n = before.size
    const r = Math.floor(i / n), c = i % n
    const row = (k: number): number => before.cells[r * n + k]!
    const col = (k: number): number => before.cells[k * n + c]!
    const filled = dir === "left" ? Array.from({ length: n }, (_, k) => row(k)).filter(Boolean).length > c
      : dir === "right" ? Array.from({ length: n }, (_, k) => row(k)).filter(Boolean).length > n - 1 - c
      : dir === "up" ? Array.from({ length: n }, (_, k) => col(k)).filter(Boolean).length > r
      : Array.from({ length: n }, (_, k) => col(k)).filter(Boolean).length > n - 1 - r
    return !filled && after.cells[i] !== 0
  }

  private newTileWords(): string {
    const i = this.newAt
    if (i < 0) return ""
    const n = this.state.size
    return `A ${this.state.cells[i]} landed at row ${Math.floor(i / n) + 1}, column ${(i % n) + 1}.`
  }

  undo(): void {
    const prev = this.past.pop()
    if (!prev) return this.say("Nothing to undo.")
    this.undos++
    this.state = prev
    this.hinted = null
    this.newAt = -1
    this.persist(); this.render(); this.say("Undone.")
  }

  /** The same opening tiles again (the daily's board, or this game's seed). Undoable. */
  restart(): void {
    const s = this.state
    if (s.moves === 0) return this.say("You haven't moved yet.")
    if (this.sentFirst && !isOver(s)) gameEvent({ type: "end", ruleset: this.ruleset, outcome: "new_game", moves: s.moves, undos: this.undos, hints: this.hints })
    this.sentFirst = false; this.undos = 0; this.hints = 0
    this.past.push(s)
    this.state = { ...deal(s.seed, s.size), attempt: newAttempt() }
    this.hinted = null; this.newAt = -1
    this.persist(); this.render()
    this.say("Started this board again. Undo brings back your moves.")
  }

  newGame(size: Size = this.state.size as Size): void {
    if (this.sentFirst && !isOver(this.state)) gameEvent({ type: "end", ruleset: this.ruleset, outcome: "new_game", moves: this.state.moves, undos: this.undos, hints: this.hints })
    this.sentFirst = false; this.undos = 0; this.hints = 0; this.hinted = null; this.newAt = -1
    if (size !== this.state.size) {
      writeJSON(PREF_KEY, size)
      const saved = readJSON<Saved>(this.saveKey(size))
      if (saved?.v === 1 && saved.state?.size === size && !isOver(saved.state)) {
        this.state = saved.state; this.past = saved.past ?? []
        this.render()
        return this.say(`${size} by ${size}: your game is where you left it.`)
      }
    }
    this.past = []
    this.state = { ...deal(randomSeed(), size), attempt: newAttempt() }
    this.persist(); this.render()
    this.say(`New ${size} by ${size} board. Slide the tiles so matching numbers meet.`)
  }

  showHint(): void {
    this.hints++
    const d = hint(this.state)
    if (!d) return this.say("No moves left. Undo takes back the last move, or choose New game.")
    this.hinted = d
    this.render()
    this.say(`Hint: try sliding ${WORDS[d]}.`)
  }

  private wire(): void {
    this.grid.addEventListener("keydown", (e) => {
      const d = KEYS[e.key]
      if (d) { e.preventDefault(); this.slide(d) }
      else if (e.key === "h" || e.key === "H") this.showHint()
    })
    // Ctrl+Z anywhere on the page: the finish card takes focus after a win, and Undo should still work there.
    document.addEventListener("keydown", (e) => {
      const t = e.target as HTMLElement | null
      if (t?.matches("input, textarea, select")) return
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); this.undo() }
    })
    // A swipe anywhere on the grid: the direction of a drag of 24 px or more.
    let start: { id: number; x: number; y: number } | null = null
    this.grid.addEventListener("pointerdown", (e) => { start = { id: e.pointerId, x: e.clientX, y: e.clientY } })
    const end = (e: PointerEvent): void => {
      if (!start || e.pointerId !== start.id) return
      const dx = e.clientX - start.x, dy = e.clientY - start.y
      start = null
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 24) return
      this.slide(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : dy > 0 ? "down" : "up")
    }
    this.grid.addEventListener("pointerup", end)
    this.grid.addEventListener("pointercancel", () => { start = null })
    this.el.querySelector("[data-t48-pad]")?.addEventListener("click", (e) => {
      const d = (e.target as HTMLElement).closest<HTMLElement>("[data-dir]")?.dataset.dir as Dir | undefined
      if (d && DIRS.includes(d)) this.slide(d)
    })
    this.toolbar?.addEventListener("click", (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act
      if (act === "undo") this.undo()
      else if (act === "hint") this.showHint()
      else if (act === "new") this.newGame()
      else if (act === "restart") this.restart()
    })
    this.sizes?.addEventListener("click", (e) => {
      const n = Number((e.target as HTMLElement).closest<HTMLElement>("[data-size]")?.dataset.size)
      if (n && n !== this.state.size) this.newGame(this.validSize(n))
    })
  }

  private say(msg: string): void { if (this.status) this.status.textContent = msg }

  private persist(): void {
    writeJSON(this.saveKey(this.state.size), { v: 1, state: this.state, past: this.past.slice(-60) } satisfies Saved)
    const best = readJSON<number>(bestKey(this.state.size)) ?? 0
    if (this.state.score > best) writeJSON(bestKey(this.state.size), this.state.score)
  }

  render(): void {
    const s = this.state
    gameProgress({ key: this.saveKey(s.size), ruleset: this.ruleset, id: `${s.size}:${s.seed}#${s.attempt ?? 0}`, moves: s.moves, won: s.reached })
    const n = s.size
    this.el.dataset.variant = String(n)
    this.sizes?.querySelectorAll<HTMLElement>("[data-size]").forEach((b) => b.setAttribute("aria-pressed", String(Number(b.dataset.size) === n)))
    this.scoreEl.textContent = s.score.toLocaleString()
    this.bestEl.textContent = Math.max(s.score, readJSON<number>(bestKey(n)) ?? 0).toLocaleString()
    if (this.grid.children.length !== n * n) {
      this.grid.style.setProperty("--n", String(n))
      this.grid.innerHTML = Array.from({ length: n * n }, (_, i) => `<div class="t48-cell" data-i="${i}"></div>`).join("")
    }
    this.grid.setAttribute("aria-label", `2048 board, ${n} by ${n}. ${s.reached ? "You have made 2048. " : ""}Arrow keys slide the tiles.`)
    this.grid.querySelectorAll<HTMLElement>(".t48-cell").forEach((cell, i) => {
      const v = s.cells[i]!
      cell.className = `t48-cell${v ? ` t48-tile v${Math.min(v, 4096)}${v > 2048 ? " vbig" : ""}` : ""}${i === this.newAt ? " is-new" : ""}`
      cell.textContent = v ? String(v) : ""
    })
    this.el.querySelectorAll<HTMLButtonElement>("[data-dir]").forEach((b) => {
      b.classList.toggle("is-hint", b.dataset.dir === this.hinted)
      b.disabled = isOver(s) || move(s, b.dataset.dir as Dir) === s
    })
    this.el.classList.toggle("is-over", isOver(s) && !canMove(s))
    this.el.classList.toggle("is-won", s.reached)
    this.el.dataset.goal = String(GOAL)
  }
}
